/**
 * Cursor pagination (DESIGN_LANGUAGE.md §6.1 "Pagination control", DL-245).
 *
 * The API pages forward only: a response carries `next_cursor` and nothing
 * else. "Previous" is therefore a stack of the cursors the reader passed
 * through, which `routes/AuditLog.tsx` and `routes/repo/History.tsx` each
 * hand-rolled. This is that stack, once: `useCursorPager()` holds it and
 * `CursorPager` renders the two buttons. Never infinite scroll (§1), and the
 * page is reset whenever a filter changes, because a cursor is bound to the
 * collection and order that produced it.
 */

import { useCallback, useState, type ReactNode } from 'react';

import { Button } from './Button';
import styles from './CursorPager.module.css';

export interface CursorPagerState {
  /** The cursor to send with the current request; undefined on the first page. */
  cursor: string | undefined;
  /** 1-based, for "Page 3". */
  page: number;
  hasPrevious: boolean;
  next: (nextCursor: string) => void;
  previous: () => void;
  reset: () => void;
}

export function useCursorPager(): CursorPagerState {
  const [cursors, setCursors] = useState<string[]>([]);
  const next = useCallback((nextCursor: string) => {
    setCursors((c) => [...c, nextCursor]);
  }, []);
  const previous = useCallback(() => {
    setCursors((c) => c.slice(0, -1));
  }, []);
  const reset = useCallback(() => {
    setCursors([]);
  }, []);
  return {
    cursor: cursors[cursors.length - 1],
    page: cursors.length + 1,
    hasPrevious: cursors.length > 0,
    next,
    previous,
    reset,
  };
}

export interface CursorPagerProps {
  pager: CursorPagerState;
  /** The current response's `next_cursor`. */
  nextCursor: string | null | undefined;
  /** "Older" / "Newer" for a chronological list; the defaults otherwise. */
  previousLabel?: string;
  nextLabel?: string;
  /** A count or note between the buttons: "Showing 50". */
  children?: ReactNode;
  /** Disabled while the next page is loading, so a double click cannot skip one. */
  busy?: boolean;
  className?: string;
}

export function CursorPager({
  pager,
  nextCursor,
  previousLabel = 'Previous',
  nextLabel = 'Next',
  children,
  busy = false,
  className,
}: CursorPagerProps) {
  if (!pager.hasPrevious && !nextCursor && !children) return null;
  return (
    <nav className={className ? `${styles.pager} ${className}` : styles.pager} aria-label="Pages">
      <Button
        size="sm"
        variant="ghost"
        disabled={!pager.hasPrevious || busy}
        onClick={pager.previous}
      >
        {previousLabel}
      </Button>
      <span className={styles.status} aria-live="polite">
        {children ?? `Page ${String(pager.page)}`}
      </span>
      <Button
        size="sm"
        variant="ghost"
        disabled={!nextCursor || busy}
        onClick={() => {
          if (nextCursor) pager.next(nextCursor);
        }}
      >
        {nextLabel}
      </Button>
    </nav>
  );
}
